import React, { useCallback, useState } from "react";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import { Company, Deal, Person, Route } from "@mapmycustomers/shared/types/entity";
import { SearchField } from "@mapmycustomers/ui";

import RelationshipType from "../../enum/RelationshipType";
import useFilteredEntities from "../../util/useFilteredEntities";
import TabLayout from "../TabLayout";

import AddNewRecordModals from "./component/AddNewRecordModals";
import Associations from "./component/Associations";
import Lists from "./component/lists/Lists";
import NoResult from "./component/NoResult";
import RelationshipsMenu from "./component/RelationshipEntityCard/RelationshipsMenu";
import IRelationships from "./type/IRelationships";

const Relationships: React.FC<IRelationships> = ({
  addChildCompaniesLoading,
  addDealsLoading,
  addParentCompanyLoading,
  addPersonsLoading,
  addRoutesLoading,
  childCompanies,
  companies,
  deals,
  entity,
  onAddToRoute,
  onChangePrimaryCompany,
  onChangePrimaryPerson,
  onFetchMoreCompanies,
  onFetchMoreDeals,
  onFetchMorePeople,
  onRemoveChildCompany,
  onRemoveDeal,
  onRemoveParentCompany,
  onRemovePeople,
  onRemoveRoute,
  onSelectChildCompanies,
  onSelectDeals,
  onSelectParentCompanies,
  onSelectParentCompany,
  onSelectPeople,
  onUnsetPrimaryCompany,
  onUnsetPrimaryPerson,
  onUpdatePrimaryCompany,
  onUpdatePrimaryPerson,
  parentCompany,
  people,
  peopleMultiSelect = true,
  primaryCompanyId,
  primaryPersonId,
  removeRouteLoadingIds,
  routes,
  supportedRelationshipTypes,
  totalCompanies,
  totalDeals,
  totalPeople,
}) => {
  const intl = useIntl();
  const [newRelationshipType, setNewRelationshipType] = useState<RelationshipType | undefined>();
  const [addNewRelationshipType, setAddNewRelationshipType] = useState<
    RelationshipType | undefined
  >();
  const [search, setSearch] = useState<string>("");

  const hideNewRelationship = useCallback(() => setNewRelationshipType(undefined), []);

  const hideRouteModal = useCallback(
    (added: boolean) => {
      setNewRelationshipType(undefined);
      if (added) {
        onAddToRoute?.();
      }
    },
    [onAddToRoute, setNewRelationshipType]
  );

  const handleSelectCompany = useCallback(
    (ids: Company["id"][], removedCompaniesIds?: Company["id"][]) => {
      if (
        newRelationshipType === RelationshipType.PARENT_COMPANY &&
        entity.entity !== EntityType.PERSON
      ) {
        onSelectParentCompany?.({ id: entity.id, parentCompanyId: ids[0] });
      } else {
        (entity.entity === EntityType.PERSON ? onSelectParentCompanies : onSelectChildCompanies)?.({
          id: entity.id,
          companyIds: ids,
          primaryCompanyId,
          removedCompaniesIds,
        });
      }
      setNewRelationshipType(undefined);
    },
    [
      entity.entity,
      entity.id,
      newRelationshipType,
      onSelectChildCompanies,
      onSelectParentCompanies,
      onSelectParentCompany,
      primaryCompanyId,
    ]
  );

  const handleSelectPeople = useCallback(
    (peopleIds: Person["id"][], removedPeopleIds?: Person["id"][]) => {
      onSelectPeople?.({ id: entity.id, peopleIds, primaryPersonId, removedPeopleIds });
    },
    [entity.id, onSelectPeople, primaryPersonId]
  );

  const [
    childCompaniesFiltered,
    dealsFiltered,
    parentCompaniesFiltered,
    peopleFiltered,
    routesFiltered,
  ] = useFilteredEntities(
    search,
    supportedRelationshipTypes.includes(RelationshipType.CHILD_COMPANY) ? childCompanies ?? [] : [],
    supportedRelationshipTypes.includes(RelationshipType.DEALS) ? deals ?? [] : [],
    supportedRelationshipTypes.includes(RelationshipType.PARENT_COMPANY) && parentCompany
      ? [parentCompany]
      : companies
      ? companies
      : [],
    supportedRelationshipTypes.includes(RelationshipType.PEOPLE) ? people ?? [] : [],
    supportedRelationshipTypes.includes(RelationshipType.ROUTES) ? routes ?? [] : []
  ) as [Company[], Deal[], Company[], Person[], Route[]];

  const isSearchEmpty = search === "";

  const noResult =
    !isSearchEmpty &&
    childCompaniesFiltered.length === 0 &&
    dealsFiltered.length === 0 &&
    parentCompaniesFiltered.length === 0 &&
    peopleFiltered.length === 0 &&
    routesFiltered.length === 0;

  const handleRemoveRoute = useCallback(
    (route: Route) => {
      onRemoveRoute?.({ id: entity.id, routeId: route.id });
    },
    [entity.id, onRemoveRoute]
  );

  const handleSelectDeals = useCallback(
    (dealIds: Deal["id"][], removedDealsIds?: Deal["id"][]) => {
      onSelectDeals?.({ id: entity.id, dealIds, removedDealsIds });
    },
    [entity.id, onSelectDeals]
  );

  const handleRemoveDeal = useCallback(
    (deal: Deal) => {
      onRemoveDeal?.(deal.id);
    },
    [onRemoveDeal]
  );

  const handleRemoveChildCompany = useCallback(
    (company: Company) => {
      onRemoveChildCompany?.(company.id);
    },
    [onRemoveChildCompany]
  );

  return (
    <TabLayout
      action={<RelationshipsMenu />}
      title={intl.formatMessage({
        id: "entityPreview.relationships.title",
        defaultMessage: "Relationships",
        description: "Title of an Relationships section on a record pane",
      })}
    >
      <SearchField
        allowClear
        onChange={setSearch}
        placeholder={intl.formatMessage({
          id: "entityPreview.relationships.search.placeholder",
          defaultMessage: "Search related records",
          description: "Search Placeholder of an Relationships section on a record pane",
        })}
        size="large"
        value={search}
      />
      <Lists
        addChildCompaniesLoading={addChildCompaniesLoading}
        addDealsLoading={addDealsLoading}
        addParentCompanyLoading={addParentCompanyLoading}
        addPersonsLoading={addPersonsLoading}
        addRoutesLoading={addRoutesLoading}
        childCompaniesFiltered={childCompaniesFiltered}
        companies={companies}
        dealsFiltered={dealsFiltered}
        entity={entity}
        isSearchEmpty={isSearchEmpty}
        onAddNewRecord={setAddNewRelationshipType}
        onChangePrimaryCompany={onChangePrimaryCompany}
        onChangePrimaryPerson={onChangePrimaryPerson}
        onChangeType={setNewRelationshipType}
        onFetchMoreCompanies={onFetchMoreCompanies}
        onFetchMoreDeals={onFetchMoreDeals}
        onFetchMorePeople={onFetchMorePeople}
        onRemoveChildCompany={handleRemoveChildCompany}
        onRemoveDeal={handleRemoveDeal}
        onRemoveParentCompany={onRemoveParentCompany}
        onRemovePeople={onRemovePeople}
        onRemoveRoute={handleRemoveRoute}
        onUnsetPrimaryCompany={onUnsetPrimaryCompany}
        onUnsetPrimaryPerson={onUnsetPrimaryPerson}
        parentCompaniesFiltered={parentCompaniesFiltered}
        peopleFiltered={peopleFiltered}
        peopleMultiSelect={peopleMultiSelect}
        removeRouteLoadingIds={removeRouteLoadingIds}
        routesFiltered={routesFiltered}
        supportedRelationshipTypes={supportedRelationshipTypes}
        totalCompanies={totalCompanies}
        totalDeals={totalDeals}
        totalPeople={totalPeople}
      />
      <Associations
        childCompanies={childCompanies}
        entity={entity}
        onHide={hideNewRelationship}
        onHideRouteModal={hideRouteModal}
        onSelectCompany={handleSelectCompany}
        onSelectDeals={handleSelectDeals}
        onSelectPeople={handleSelectPeople}
        onUpdatePrimaryCompany={onUpdatePrimaryCompany}
        onUpdatePrimaryPerson={onUpdatePrimaryPerson}
        parentCompany={parentCompany}
        people={people}
        peopleMultiSelect={peopleMultiSelect}
        primaryCompanyId={primaryCompanyId}
        primaryPersonId={primaryPersonId}
        query={search}
        relationshipType={newRelationshipType}
      />
      <AddNewRecordModals
        childCompanies={childCompanies}
        deals={deals}
        entity={entity}
        onHide={setAddNewRelationshipType}
        onSelectChildCompanies={onSelectChildCompanies}
        onSelectDeals={handleSelectDeals}
        onSelectParentCompanies={onSelectParentCompanies}
        onSelectParentCompany={onSelectParentCompany}
        onSelectPeople={handleSelectPeople}
        parentCompany={parentCompany}
        people={people}
        primaryCompanyId={primaryCompanyId}
        primaryPersonId={primaryPersonId}
        relationshipType={addNewRelationshipType}
      />
      {noResult && (
        <NoResult
          entity={entity}
          onAdd={setNewRelationshipType}
          supportedRelationshipTypes={supportedRelationshipTypes}
        />
      )}
    </TabLayout>
  );
};

export default Relationships;
